import { ChangeDetectionStrategy, Component, inject, Input, input, InputSignal, signal} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { RestService } from 'src/app/services/rest.service';
import { SurveyHeaderComponent } from './survey-header/survey-header.component';
import { Subscription } from 'rxjs';
import Swal from "sweetalert2";
import { ToolsService } from 'src/app/services/tools.service';

@Component({
    selector: 'survey',
    standalone: true,
    imports: [
        CommonModule,
        IonicModule,
        TranslateModule,
        ReactiveFormsModule,
        SurveyHeaderComponent
    ],
    templateUrl: './new-survey.component.html',
    styleUrl: './new-survey.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class SurveyComponent{
    @Input('isModal') isModal: boolean = false;
    @Input('registerID') registerID: any = null;
    @Input('surveyModal') surveyModal: any = null;

    private tools = inject(ToolsService);
    private routeService = inject(ActivatedRoute);
    private modalController = inject(ModalController);
    private router = inject(Router);
    private translate = inject(TranslateService);
    private rest = inject(RestService);
    private fb = inject(FormBuilder);
    private langSubscription: Subscription;
    private surveyId: any;
    private structure: any;
    private greetingsMessage: any;
    
    public languages: any = [];
    public surveyForm: FormGroup;
    public surveyStyle: any;
    public surveyName: any;
    public surveyLogo: any;
    public formData: any;
    public showForm = signal(false)

    ngOnInit(){
      if (this.isModal){
        if(!this.surveyModal?.structure)
          return;

        this.surveyId = this.surveyModal?.id;
        this.structure = this.surveyModal?.structure;
        this.surveyStyle = this.surveyModal?.style;
        this.surveyName = this.surveyModal?.name;
        this.surveyLogo = this.surveyModal?.style?.logo?.url ? `${this.rest.apiUrlBase}/${this.surveyModal.style.logo.url}` : null
        this.greetingsMessage = this.surveyModal?.greeting;

        this.initForm();
        return;
      }
      
      this.surveyId = this.routeService.snapshot.paramMap.get('id');
      const defaultLang = this.routeService.snapshot.paramMap.get('lang') || this.translate.currentLang || 'es';
      this.getRestInfo(this.surveyId, defaultLang);
    }

    initSurvey() {
      this.langSubscription = this.translate.onLangChange.subscribe((event) => { this.initForm() });
      this.initForm();
    }

    initForm() {
      this.surveyForm = this.fb.group({});
      this.formData = this.structure?.[this.translate.currentLang]?.map((e, index) => {
        let options = [];
        switch (e.type) {
          case "satisfaction_level_3": options = [1, 2, 3]; break;
          case "satisfaction_level_5": options = [1, 2, 3, 4, 5]; break;
        }
        return {
          key: `${index}-${e.type}`,
          type: e.type,
          title: e.name || e.title,
          options: e.options || options,
        };
      });
  
      if (!this.formData) {
        this.formData = [];
        return;
      }
  
      this.formData.forEach((data) => {
        if (data.type !== "selection_multi") {
          this.surveyForm.addControl(data.key, new FormControl("", Validators.required));
        } 
        else {
          const multi = this.fb.group({ items: this.fb.array([]) });
          const itemsFormArray = multi.get("items") as FormArray;
          data.options.forEach((option) => {
            itemsFormArray.push(this.fb.group({value: option, selected: this.fb.control(false)}));
          });
          this.surveyForm.addControl(data.key, multi);
        }
      });
      this.showForm.set(true);
    }

    setSelectedOption(option: any, key: string) {
      this.surveyForm.patchValue({ key, value: option });
    }

    getMultiControls(key) {
      const form = this.surveyForm.get(key);
      const itemsForm = form.get('items') as FormArray
      return itemsForm.controls;
    }

    getFormData(){
      const formArray = Object.values(this.surveyForm.value);
      const array = this.formData?.map((element, index) => ({ ...element, answer: formArray.length > index ? formArray[index] : "" }));

      return array?.map((e) => {
        if (e.type == "selection_multi") 
          e.answer = e.answer?.items?.filter(e => e.selected).map(answer => { return answer.value; });
  
        if (e.type == "satisfaction_level_3") 
          e.answer = (e.answer === "" || e.answer === null) ? null : `${e.answer}/3`;
  
        if (e.type == "satisfaction_level_5") 
          e.answer = (e.answer === "" || e.answer === null) ? null : `${e.answer}/5`;
  
        return { question: e.title, type: e.type, answer: e.answer };
      });
    }

    getRestInfo(id, lang) {
      this.rest.getSurvey(id).subscribe((element) => {
        if (!element.data?.langs?.includes(lang))
          this.translate.use(element?.data?.main_language || 'es');
        else
          this.translate.use(lang);
  
        const survey = element.data || undefined;
        this.surveyStyle = survey?.style;
        this.languages = survey?.langs || [];
        this.tools.setLanguages(survey?.langs || []);
        this.structure = survey?.structure;
        this.greetingsMessage = survey?.greeting;
        this.surveyLogo = survey?.style?.logo?.url
          ? `${this.rest.apiUrlBase}/${survey.style.logo.url}`
          : null

        if (element.status == "KO" || !survey) {
          this.router.navigateByUrl("not-found");
          return;
        }

        localStorage.setItem("survey", JSON.stringify(survey));
        this.initSurvey();
      });
    }

    sendSurvey() {
      const formData = this.getFormData();
      const send = { 
        register_id: this.registerID, 
        survey_id: this.surveyId, 
        data: formData 
      };
      
      this.rest.saveSurvey(send).subscribe((response) => {
        Swal.fire({
            title: "",
            text: this.translate.instant("Encuesta enviada correctamente"),
            icon: "success",
            showCancelButton: false,
            showConfirmButton: false,
            timer: 10000,
            timerProgressBar: true,
            footer: this.greetingsMessage?.[this.translate.currentLang],
        }).then((result) => {
            if (this.isModal){
              this.dismissModal()
              return;
            }

            if (result.dismiss === Swal.DismissReason.timer || result.dismiss === Swal.DismissReason.backdrop) {
                location.reload();
            }
        });
      });
    }

    dismissModal(){
      this.modalController.dismiss();
    }

    ngOnDestroy(){
      this.langSubscription?.unsubscribe();
    }
}